import React from 'react';
import { ColumnActionsMode, IColumn } from '@fluentui/react';
import { format } from 'date-fns';
import { IColumnFilterMenuProps, IColumnFilterOption } from '../components/ColumnFilterMenu';

export type ColumnFilterValue = string | number | Date | null | undefined;

export interface IColumnFilterDefinition<T>
{
    /** Værdien der filtreres og sorteres på */
    getValue: (item: T) => ColumnFilterValue;
    /** Teksten i filtermenuen. Standard er værdien som tekst - datoer som dd-MM-yyyy */
    format?: (value: string | number | Date) => string;
    /** Sortering i filtermenuen. Standard er faldende for tal og datoer, ellers stigende */
    sort?: 'asc' | 'desc';
}

/** Nøglen er kolonnens key */
export type ColumnFilterDefinitions<T> = { [columnKey: string]: IColumnFilterDefinition<T> };

const EMPTY_VALUE_TEXT = '(tom)';

const isEmpty = (value: ColumnFilterValue) : boolean => value === null || value === undefined || value === '';

const toText = <T>(definition: IColumnFilterDefinition<T>, item: T) : string => {
  const value = definition.getValue(item);
  if(isEmpty(value)) return '';
  if(definition.format) return definition.format(value as string | number | Date);

  return value instanceof Date ? format(value, "dd-MM-yyyy") : String(value);
}

// Filtrering skelner ikke mellem store/små bogstaver og mellemrum før/efter - "LARS" og "Lars " er samme værdi
const toKey = (text: string) : string => text.trim().toLocaleLowerCase('da');

// Tal, datoer og tekst der kun består af tal, f.eks. "2017" og "2024_1"
const isNumberLike = (value: ColumnFilterValue) : boolean =>
  typeof value === 'number' || value instanceof Date || (typeof value === 'string' && /^\d+([_.,-]\d+)*$/.test(value));

const compareValues = (a: ColumnFilterValue, b: ColumnFilterValue, direction: 'asc' | 'desc') : number => {
  // Tomme værdier står altid øverst
  if(isEmpty(a) || isEmpty(b)) return isEmpty(a) === isEmpty(b) ? 0 : isEmpty(a) ? -1 : 1;

  const sign = direction === 'desc' ? -1 : 1;
  if(a instanceof Date && b instanceof Date) return sign * (a.getTime() - b.getTime());
  if(typeof a === 'number' && typeof b === 'number') return sign * (a - b);

  // numeric: "2024_2" før "2024_10" og "9" før "10"
  return sign * String(a).localeCompare(String(b), 'da', { numeric: true });
}

/**
 * Filtre på kolonner i en DetailsList, hvor brugeren vælger én eller flere værdier i kolonneoverskriften.
 * Alle filtre skal være opfyldt. Uden aktive filtre returneres listen uændret (samme reference).
 */
export default function useColumnFilters<T>(items: T[] | undefined, definitions: ColumnFilterDefinitions<T>) {
    const [filters, setFilters] = React.useState<{ [columnKey: string]: string[] }>({});
    const [menu, setMenu] = React.useState<{ columnKey: string, target: HTMLElement } | undefined>(undefined);

    // Definitionerne oprettes typisk på ny ved hver render - ref'en holder filtreringen stabil
    const definitionsRef = React.useRef(definitions);
    definitionsRef.current = definitions;

    const activeFilters = Object.entries(filters).filter(([columnKey, values]) => values.length > 0 && definitions[columnKey]);
    const hasFilters = activeFilters.length > 0;

    const filteredItems = React.useMemo(() : T[] | undefined => {
      const active = Object.entries(filters).filter(([columnKey, values]) => values.length > 0 && definitionsRef.current[columnKey]);
      if(items === undefined || active.length === 0) return items;

      return items.filter(item =>
        active.every(([columnKey, values]) => values.includes(toKey(toText(definitionsRef.current[columnKey], item))))
      );
    }, [items, filters]);

    const toggleValue = (columnKey: string, value: string) => {
      setFilters(prev => {
        const current = prev[columnKey] ?? [];
        return { ...prev, [columnKey]: current.includes(value) ? current.filter(v => v !== value) : [...current, value] };
      });
    }

    const clearColumn = (columnKey: string) => {
      setFilters(prev => ({ ...prev, [columnKey]: [] }));
    }

    const clearFilters = () => {
      setFilters({});
    }

    const getOptions = (columnKey: string) : IColumnFilterOption[] => {
      const definition = definitions[columnKey];
      // Pr. nøgle: værdien der sorteres på og hvor mange gange hver skrivemåde forekommer
      const options = new Map<string, { value: ColumnFilterValue, spellings: Map<string, number> }>();

      (items ?? []).forEach(item => {
        const text = toText(definition, item).trim();
        const key = toKey(text);
        if(!options.has(key)) options.set(key, { value: definition.getValue(item), spellings: new Map() });

        const spellings = options.get(key)!.spellings;
        spellings.set(text, (spellings.get(text) ?? 0) + 1);
      });

      // Valgte værdier vises også, selvom de ikke findes i listen, så de kan fravælges
      (filters[columnKey] ?? []).forEach(key => {
        if(!options.has(key)) options.set(key, { value: key, spellings: new Map([[key, 1]]) });
      });

      const values = Array.from(options.values()).map(option => option.value).filter(value => !isEmpty(value));
      const direction = definition.sort ?? (values.length > 0 && values.every(isNumberLike) ? 'desc' : 'asc');

      return Array.from(options.entries())
        .sort(([, a], [, b]) => compareValues(a.value, b.value, direction))
        .map(([key, option]) => {
          // Den hyppigste skrivemåde vises i menuen
          const text = Array.from(option.spellings.entries()).sort((a, b) => b[1] - a[1])[0][0];
          return { value: key, text: key === '' ? EMPTY_VALUE_TEXT : text };
        });
    }

    /** Tilføjer filtermenu og filterikon til de kolonner, der har en definition */
    const applyToColumns = (columns: IColumn[]) : IColumn[] => {
      return columns.map(column => definitions[column.key] ? {
        ...column,
        columnActionsMode: ColumnActionsMode.hasDropdown,
        isFiltered: (filters[column.key]?.length ?? 0) > 0,
        onColumnClick: (ev: React.MouseEvent<HTMLElement>) => { setMenu({ columnKey: column.key, target: ev.currentTarget }); }
      } : column);
    }

    /** Props til ColumnFilterMenu - undefined når menuen er lukket */
    const filterMenuProps: IColumnFilterMenuProps | undefined = menu && definitions[menu.columnKey] ? {
      target: menu.target,
      options: getOptions(menu.columnKey),
      selected: filters[menu.columnKey] ?? [],
      onToggle: (value: string) => { toggleValue(menu.columnKey, value); },
      onClear: () => { clearColumn(menu.columnKey); },
      onClearAll: hasFilters ? () => { clearFilters(); } : undefined,
      onDismiss: () => { setMenu(undefined); }
    } : undefined;

    return {
        filteredItems,
        applyToColumns,
        filterMenuProps,
        hasFilters,
        clearFilters
    }
}
